const LIST_READ_DEADLINE_MS = 30_000;

// Bound the whole read, including an interceptor waiting for token refresh.
// Axios's per-attempt timeout alone cannot bound that wait.
async function readWithDeadline<T>(
  read: (signal: AbortSignal) => Promise<T>,
  controller: AbortController,
): Promise<T> {
  if (controller.signal.aborted) throw new Error('List read cancelled');
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort = (): void => undefined;
  const interrupted = new Promise<never>((_, reject) => {
    onAbort = () => reject(new Error('List read cancelled'));
    controller.signal.addEventListener('abort', onAbort, { once: true });
    timer = setTimeout(() => {
      reject(new Error('List read deadline exceeded'));
      controller.abort();
    }, LIST_READ_DEADLINE_MS);
  });

  try {
    const pendingRead = Promise.resolve().then(() => {
      if (controller.signal.aborted) throw new Error('List read cancelled');
      return read(controller.signal);
    });
    return await Promise.race([pendingRead, interrupted]);
  } finally {
    clearTimeout(timer);
    controller.signal.removeEventListener('abort', onAbort);
    // Also cancel a sibling GET if a combined read has failed.
    controller.abort();
  }
}

export { readWithDeadline };
